/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { UserProfile } from '@app-libs/model';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { ProfileApiService } from '../api/profile-api.service';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  // Cache the user profile
  private userProfile = new BehaviorSubject<UserProfile | null>(null);

  constructor(private profileApiService: ProfileApiService) { }

  async loadUserProfile(userId: string): Promise<UserProfile | null> {
    // console.debug('[loadUserProfile] Loading user profile:', userId);
    try {
      const profile = await firstValueFrom(this.profileApiService.getProfile());
      this.userProfile.next(profile);
      return profile;
    } catch (err) {
      console.error('[loadUserProfile] Error:', err);
      this.userProfile.next(null);
      return null;
    }
  }

  async updateUserProfile(
    userId: string,
    profileData: Partial<UserProfile>
  ): Promise<UserProfile | null> {
    // console.debug('[updateUserProfile] Updating user profile:', userId, profileData);
    try {
      // Use the basic update endpoint for safe user-editable fields
      // Filter out null values since the API expects string | undefined
      type BasicProfileFields = Pick<UserProfile, 'first_name' | 'last_name' | 'additional_info'>;
      const basicFields: Partial<BasicProfileFields> = {};

      if (profileData.first_name !== null && profileData.first_name !== undefined) {
        basicFields.first_name = profileData.first_name;
      }
      if (profileData.last_name !== null && profileData.last_name !== undefined) {
        basicFields.last_name = profileData.last_name;
      }
      if (profileData.additional_info !== null && profileData.additional_info !== undefined) {
        basicFields.additional_info = profileData.additional_info;
      }

      const profile = await firstValueFrom(this.profileApiService.updateProfileBasic(basicFields));
      this.userProfile.next(profile);
      return profile;
    } catch (err) {
      console.error('[updateUserProfile] Error:', err);
      return null;
    }
  }

  async updateSelectedWorkspace(
    userId: string,
    selectedWorkspace: string
  ): Promise<UserProfile | null> {
    try {
      const profile = await firstValueFrom(this.profileApiService.updateSelectedWorkspace(selectedWorkspace));
      this.userProfile.next(profile);
      return profile;
    } catch (err) {
      console.error('[updateUserProfile] Error:', err);
      return null;
    }
  }

  getUserProfile(): Observable<UserProfile | null> {
    return this.userProfile.asObservable().pipe(
      filter((profile) => profile !== null) // Only emit when profile is not null
    );
  }
}
